/**
 * AucChart.jsx — Exposition cumulée aux particules fines (AUC)
 *
 * Pourquoi l'AUC ?
 * La moyenne peut masquer des épisodes courts mais intenses.
 * L'AUC (Area Under the Curve, µg/m³·h) quantifie la charge totale
 * d'exposition sur la période — métrique standard en médecine environnementale.
 * Suggestion de Dr. Emmanuelle Van Erck (3 avril 2026).
 *
 * Calcul : intégration trapézoïdale
 *   AUC = Σ (PM[i] + PM[i+1]) / 2 × Δt(h)
 *
 * Affichage :
 *   - AUC totale PM1 / PM2.5 / PM10 (bandeau récapitulatif)
 *   - Graphique barres : AUC journalière PM2.5 + PM10 (si session ≥ 2 jours)
 *   - Baseline indicative : 25 µg/m³ × 24h = 600 µg/m³·h/jour (seuil de référence journalier PM2.5)
 *
 * Note : pas de seuil AUC standardisé en médecine équine à ce jour.
 * Indicateur d'exposition relative — à interpréter par un vétérinaire équin.
 */
import React, { useMemo } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Bar } from 'react-chartjs-2';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../constants/theme';
import { semanticColors } from '../constants/semanticColors';
import { getAucBaselineDay } from '../constants/pmThresholds';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const isMeasured = (v) => typeof v === 'number' && Number.isFinite(v);

// Intégration trapézoïdale — retourne AUC en µg/m³·h, ou null si la grandeur
// n'est jamais mesurée (une AUC de 0 « dans la norme » serait fausse).
// Série partiellement mesurée : les intervalles bordés d'un null sont ignorés.
function trapezoidalAUC(readings, field) {
  if (!readings || !readings.some(r => isMeasured(r[field]))) return null;
  if (readings.length < 2) return 0;
  let auc = 0;
  for (let i = 1; i < readings.length; i++) {
    const v1 = readings[i - 1][field];
    const v2 = readings[i][field];
    if (v1 == null || v2 == null) continue;
    const t1 = new Date(readings[i - 1].timestamp).getTime();
    const t2 = new Date(readings[i].timestamp).getTime();
    const dtH = (t2 - t1) / 3_600_000; // ms → heures
    auc += ((v1 + v2) / 2) * dtH;
  }
  return auc;
}

// Grouper les readings par jour. La clé 'JJ/MM' (fuseau du navigateur, D201) sert AU BUCKETING
// et au tri chronologique ci-dessous (split('/')) — c'est une CLÉ TECHNIQUE, PAS un
// libellé localisable : NON traduite (i18n-owner O3). Format DD/MM identique en fr-FR
// et en-GB, donc l'axe (labels = clés triées) reste correct dans les deux langues.
function groupByDay(readings) {
  const days = {};
  readings.forEach(r => {
    if (!r.timestamp) return;
    const d = new Date(r.timestamp).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit'
    });
    if (!days[d]) days[d] = [];
    days[d].push(r);
  });
  return days;
}

const AucChart = ({ readings, darkMode = false }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const c = {
    text:    darkMode ? '#E1E1E1' : '#2C3E50',
    textSec: darkMode ? '#B8BFC7' : '#6B7280',
    card:    darkMode ? '#1E1E32' : '#fff',
    border:  darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    grid:    darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  };

  const { totalAUC, dailyData, nbDays } = useMemo(() => {
    if (!readings || readings.length < 2) {
      return { totalAUC: null, dailyData: null, nbDays: 0 };
    }

    // AUC totales
    const totalAUC = {
      pm1:  trapezoidalAUC(readings, 'pm1'),
      pm25: trapezoidalAUC(readings, 'pm2_5'),
      pm10: trapezoidalAUC(readings, 'pm10'),
    };

    // AUC par jour
    const byDay = groupByDay(readings);
    const sortedDays = Object.keys(byDay).sort((a, b) => {
      // Tri chronologique (format JJ/MM)
      const [da, ma] = a.split('/').map(Number);
      const [db, mb] = b.split('/').map(Number);
      return ma !== mb ? ma - mb : da - db;
    });

    const dailyData = {
      labels: sortedDays,
      pm25:   sortedDays.map(d => trapezoidalAUC(byDay[d], 'pm2_5')),
      pm10:   sortedDays.map(d => trapezoidalAUC(byDay[d], 'pm10')),
    };

    return { totalAUC, dailyData, nbDays: sortedDays.length };
  }, [readings]);

  if (!totalAUC) return (
    <div style={{
      background: darkMode ? '#1E1E32' : '#fff',
      borderRadius: '16px', padding: '24px',
      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
      marginBottom: '24px'
    }}>
      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: '600',
        color: darkMode ? '#E1E1E1' : '#2C3E50', margin: '0 0 8px' }}>
        {t('charts.auc.title')}
      </h3>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px',
        color: darkMode ? '#B8BFC7' : '#6B7280', margin: 0 }}>
        {t('charts.auc.insufficient')}
        {readings && readings.length < 2 && ' ' + t('charts.auc.insufficientCount')}
      </p>
    </div>
  );

  // Baseline indicative dérivée du JSON (pm25.good × pm_hours = 25 × 24 = 600) — cf. getAucBaselineDay
  const BASELINE_DAY = getAucBaselineDay();

  const fmt = (v) => v < 1000 ? v.toFixed(0) : `${(v/1000).toFixed(1)}k`;

  // Couleur sémantique texte (AAA sur fond clair) — utilisée aussi pour border tint.
  // Grandeur non mesurée → neutre, ni vert ni rouge.
  const aucColor = (auc, days) => {
    if (auc === null) return semanticColors.neutralText;
    if (days === 0) return '#6B7280';
    const perDay = auc / days;
    if (perDay <= BASELINE_DAY)       return semanticColors.greenText;
    if (perDay <= BASELINE_DAY * 1.5) return semanticColors.amberText;
    return semanticColors.redText;
  };

  const BadgeAUC = ({ label, auc, color }) => (
    <div style={{
      flex: '1 1 140px', background: darkMode ? '#282845' : '#F8FAFF',
      borderRadius: '10px', padding: '14px 16px',
      border: `2px solid ${color}20`, textAlign: 'center'
    }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color, textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: auc === null ? '16px' : '26px', fontWeight: '700', color: c.text, lineHeight: 1 }}>
        {auc === null ? t('common.notMeasured') : fmt(auc)}
      </div>
      {auc !== null && <div style={{ fontSize: '10px', color: c.textSec, marginTop: '4px' }}>µg/m³·h</div>}
    </div>
  );

  const showDailyChart = nbDays >= 2;

  // Une grandeur jamais mesurée n'a pas de série journalière (pas de barres à 0).
  const chartData = showDailyChart ? {
    labels: dailyData.labels,
    datasets: [
      {
        label: 'PM2.5 (µg/m³·h)',
        data: dailyData.pm25,
        backgroundColor: 'rgba(139, 92, 246, 0.75)',
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: 'PM10 (µg/m³·h)',
        data: dailyData.pm10,
        backgroundColor: 'rgba(236, 72, 153, 0.75)',
        borderRadius: 4,
        borderSkipped: false,
      },
    ].filter(ds => ds.data.some(v => v !== null))
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { bottom: 8 } },
    plugins: {
      legend: {
        labels: { color: c.text, font: { family: 'Inter', size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: ctx => ctx.parsed.y == null ? null : ` ${ctx.dataset.label}: ${ctx.parsed.y.toFixed(0)} µg/m³·h`,
          afterBody: () => [t('charts.auc.baselineTooltip', { v: BASELINE_DAY })]
        }
      },
      zoom: {
        pan:  { enabled: true, mode: 'x', modifierKey: 'shift' },
        zoom: {
          wheel: { enabled: true, speed: 0.1 },
          pinch: { enabled: true },
          mode: 'x'
        },
        limits: { x: { min: 'original', max: 'original' } }
      },
    },
    scales: {
      x: {
        ticks: { color: c.textSec, font: { family: 'Inter', size: 10 } },
        grid:  { color: c.grid },
      },
      y: {
        ticks: {
          color: c.textSec, font: { family: 'Inter', size: 10 },
          callback: v => `${v} `
        },
        grid:  { color: c.grid },
        title: { display: true, text: 'µg/m³·h', color: c.textSec, font: { size: 10 } }
      }
    }
  };

  return (
    <div style={{
      background: c.card, borderRadius: '16px', padding: '24px',
      border: `1px solid ${c.border}`,
      boxShadow: darkMode ? 'none' : '0 2px 12px rgba(0,0,0,0.06)',
      marginBottom: '20px'
    }}>
      {/* Titre */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: '700',
          color: BRAND.blueText, margin: 0 }}>
          {t('charts.auc.title')}
        </h3>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: c.textSec,
          margin: '4px 0 0' }}>
          {t('charts.auc.subtitle')}
          {nbDays >= 2 && t('charts.auc.subtitleDays', { count: nbDays })}
        </p>
      </div>

      {/* Badges AUC totaux */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: showDailyChart ? '24px' : '8px' }}>
        <BadgeAUC label={t('charts.auc.badgeTotal', { pm: 'PM1' })}   auc={totalAUC.pm1}  color={aucColor(totalAUC.pm1,  nbDays)} />
        <BadgeAUC label={t('charts.auc.badgeTotal', { pm: 'PM2.5' })} auc={totalAUC.pm25} color={aucColor(totalAUC.pm25, nbDays)} />
        <BadgeAUC label={t('charts.auc.badgeTotal', { pm: 'PM10' })}  auc={totalAUC.pm10} color={aucColor(totalAUC.pm10, nbDays)} />
      </div>

      {/* Graphique barres journalier */}
      {showDailyChart && (
        <>
          <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: '600',
            color: c.text, margin: '0 0 12px' }}>
            {t('charts.auc.dailyTitle', { pm: 'PM2.5 / PM10' })}
          </h4>
          <div style={{ height: '240px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
        <button type="button"
          onClick={() => { if (chartRef.current) chartRef.current.resetZoom(); }}
          style={{ padding: '5px 12px', background: '#1B77F1', color: '#fff',
            border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
          {t('common.zoom.reset')}
        </button>
      </div>
          <Bar ref={chartRef} data={chartData} options={chartOptions} />
      {/* Instructions zoom */}
      <div style={{
        marginTop: '12px', padding: '8px 12px',
        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(27, 119, 241, 0.05)',
        borderRadius: '6px',
        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(27, 119, 241, 0.15)',
        fontSize: '13px', color: darkMode ? '#E8EDF2' : '#4A5568',
        textAlign: 'center', lineHeight: '1.5'
      }}>
        <span style={{ display: 'none' }} className="mobile-only">
          💡 {t('common.zoom.touch')}
        </span>
        <span className="desktop-only">
          💡 {t('common.zoom.desktop')}
        </span>
      </div>
          </div>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '10px', color: c.textSec,
            margin: '16px 0 0', fontStyle: 'italic' }}>
            {t('charts.auc.baselineNote', { v: BASELINE_DAY })}
          </p>
        </>
      )}
    </div>
  );
};

export default AucChart;
